import CustomSubUIView from '@src/dataObjects/CustomSubUIView'
import { ApiParameterBinding, ContentAlign, Filter, FilterComponent, getApiClient, getApplicationContext, getRelationshipCaption, Model, Sort, SortDirection, StyleRuleContentDisplayMode, UIViewEventArgs, ViewModel, ViewModelProperty, ViewModelPropertyState, ViewModelState } from '@neos/core'
import { cast, compareValue, emptyGuid, getDefaultValueByType, getFirstArrayElementOrDefault, getLocalizator, initialize, Localizator, stringFormat, Task, toCamelCase, toRecord } from '@neos/shared'
import { getApplicationResourceManager } from '@neos/app'
import CustomSubUIViewUI from '@src/views/CustomSubUIViewUI/CustomSubUIViewUI'
import { CustomViewContentAlign } from '@src/enums/CustomViewContentAlign'
import { CustomViewStyleRuleContentDisplayMode } from '@src/enums/CustomViewStyleRuleContentDisplayMode'
import { CustomViewFilterComponent } from '@src/enums/CustomViewFilterComponent'
import CustomUIViewCreateUI from '@src/views/CustomUIViewCreateUI/CustomUIViewCreateUI'
import CustomUIViewStyleRuleUI from '@src/views/CustomUIViewStyleRuleUI/CustomUIViewStyleRuleUI'
import CustomSubUIViewStyleRuleUI from '@src/views/CustomSubUIViewStyleRuleUI/CustomSubUIViewStyleRuleUI'
import CustomSubUIViewPropertyUI from '@src/views/CustomSubUIViewPropertyUI/CustomSubUIViewPropertyUI'
import CustomSubUIViewEntityViewParameterUI from '@src/views/CustomSubUIViewEntityViewParameterUI/CustomSubUIViewEntityViewParameterUI'
import CustomUIViewDataFilterUI from '@src/views/CustomUIViewDataFilterUI/CustomUIViewDataFilterUI'
import { typeEquivalenceModuleFilterOperatorFromCoreFilterOperator } from '@src/sharedMethods/typeEquivalence'
import CustomUIViewPropertyUI from '@src/views/CustomUIViewPropertyUI/CustomUIViewPropertyUI'
import CustomUIViewEntityViewParameterUI from '@src/views/CustomUIViewEntityViewParameterUI/CustomUIViewEntityViewParameterUI'
import CustomUIViewDetailView from '@src/entityViews/CustomUIViewDetailView'
import CustomUIViewGroupsUIViewModel from '@src/views/CustomUIViewGroupsUI/CustomUIViewGroupsUIViewModel'
import CustomUIViewGroupsUI from '@src/views/CustomUIViewGroupsUI/CustomUIViewGroupsUI'

/**
 * Build sub UI view nodes.
 * 
 * @param ownerViewModel Owner view model.
 * @param parentPath Parent path.
 */
export function uiCustomizationBuildSubUIViewNodes(ownerViewModel: ViewModel<Model>, parentPath: string[]): CustomSubUIView[] {
  let nodes: CustomSubUIView[] = [];
  if (parentPath.length >= uiCustomizationMaxSubUIViewDepth()) {
    return nodes;
  }
  for (let relation of ownerViewModel.relationships) {
    let subViewModel: (ViewModel<Model> | null | undefined) = ownerViewModel.findSubViewModel<Model>(relation.path);
    if (subViewModel == null || uiCustomizationIsCustomViewDisabled(subViewModel)) {
      continue;
    }
    let relationPath: string[] = [...[...parentPath, relation.path]];
    let caption: (string | null | undefined) = getRelationshipCaption(ownerViewModel, relation) ?? relation.path;
    if (parentPath.indexOf(relation.path) >= 0) {
      caption = stringFormat(getApplicationResourceManager('TaskScheduler').get('modules.UICustomization.CustomViewSubUIViewLevel'), caption, relationPath.length);
    }
    let children = uiCustomizationBuildSubUIViewNodes(subViewModel, relationPath);
    nodes.push(initialize(new CustomSubUIView(),{
  name : relation.path, caption : caption, relationPath : relationPath, isGridPropertiesVisible : !subViewModel.monoRecord, children : children.length > 0 ? children : null
  }));
  }
  return nodes;
}

/**
 * Build sub UI view rows.
 * 
 * @param ownerViewModel Owner view model.
 * @param parentPath Parent path.
 */
export function uiCustomizationBuildSubUIViewRows(ownerViewModel: ViewModel<Model>, parentPath: string[]): CustomSubUIViewUI[] {
  let rows: CustomSubUIViewUI[] = [];
  if (parentPath.length >= uiCustomizationMaxSubUIViewDepth()) {
    return rows;
  }
  for (let relation of ownerViewModel.relationships) {
    let subViewModel: (ViewModel<Model> | null | undefined) = ownerViewModel.findSubViewModel<Model>(relation.path);
    if (subViewModel == null || uiCustomizationIsCustomViewDisabled(subViewModel)) {
      continue;
    }
    let relationPath: string[] = [...[...parentPath, relation.path]];
    rows.push(new CustomSubUIViewUI({parentCollectionName : (relationPath).join('/')}));
    for (let descendant of uiCustomizationBuildSubUIViewRows(subViewModel, relationPath)) {
      rows.push(descendant);
    }
  }
  return rows;
}

/**
 * Convert content align.
 * 
 * @param contentAlign Content align.
 */
export function uiCustomizationConvertContentAlign(contentAlign: (ContentAlign | null | undefined)): CustomViewContentAlign {
  switch (contentAlign) {
  case ContentAlign.start:
  return CustomViewContentAlign.start;
  case ContentAlign.center:
  return CustomViewContentAlign.center;
  case ContentAlign.end:
  return CustomViewContentAlign.end;
  default:
  return null;
  }
}

/**
 * Convert content display mode.
 * 
 * @param contentDisplayMode Content display mode.
 */
export function uiCustomizationConvertContentDisplayMode(contentDisplayMode: (StyleRuleContentDisplayMode | null | undefined)): CustomViewStyleRuleContentDisplayMode {
  switch (contentDisplayMode) {
  case StyleRuleContentDisplayMode.iconBeforeText:
  return CustomViewStyleRuleContentDisplayMode.iconBeforeText;
  case StyleRuleContentDisplayMode.iconAfterText:
  return CustomViewStyleRuleContentDisplayMode.iconAfterText;
  case StyleRuleContentDisplayMode.icon:
  return CustomViewStyleRuleContentDisplayMode.icon;
  default:
  return null;
  }
}

/**
 * Convert filter component.
 * 
 * @param filterComponent Filter component.
 */
export function uiCustomizationConvertFilterComponent(filterComponent: FilterComponent): CustomViewFilterComponent {
  switch (filterComponent) {
  case FilterComponent.calendarDay:
  return CustomViewFilterComponent.calendarDay;
  case FilterComponent.calendarMonth:
  return CustomViewFilterComponent.calendarMonth;
  case FilterComponent.calendarYear:
  return CustomViewFilterComponent.calendarYear;
  default:
  return CustomViewFilterComponent.default;
  }
}

/**
 * Disable custom view.
 * 
 * @param viewModel View model.
 */
export function uiCustomizationDisableCustomView(viewModel: ViewModel<Model>): void {
  viewModel.contextData.set('DisableCustomView', true);
}

/**
 * Find owner view model.
 * 
 * @param viewModel View model.
 * @param subViewName Sub view name.
 */
export function uiCustomizationFindOwnerViewModel(viewModel: ViewModel<Model>, subViewName: (string | null | undefined)): (ViewModel<Model> | null | undefined) {
  let ownerViewModel = uiCustomizationGetOwnerViewModel(viewModel, null);
  if (subViewName == null) {
    return ownerViewModel;
  }
  let current = ownerViewModel;
  for (let segment of subViewName.split('/')) {
    let subViewModel: (ViewModel<Model> | null | undefined) = current.findSubViewModel<Model>(segment);
    if (subViewModel == null) {
      return null;
    }
    current = subViewModel;
  }
  return current;
}

/**
 * Get entity view parameter key.
 * 
 * @param parameterName Parameter name.
 * @param parameterValue Parameter value.
 */
export function uiCustomizationGetEntityViewParameterKey(parameterName: (string | null | undefined), parameterValue: (string | null | undefined)): string {
  return JSON.stringify([parameterName, parameterValue]);
}

/**
 * Get missing sub UI view rows.
 * 
 * @param customView Custom view.
 * @param ownerViewModel Owner view model.
 */
export function uiCustomizationGetMissingSubUIViewRows(customView: CustomUIViewCreateUI, ownerViewModel: ViewModel<Model>): CustomSubUIViewUI[] {
  let missingRows: CustomSubUIViewUI[] = [];
  for (let expectedRow of uiCustomizationBuildSubUIViewRows(ownerViewModel, [])) {
    let expectedPath = expectedRow.parentCollectionName.toUpperCase();
    let storedRow = getFirstArrayElementOrDefault(customView.subUIViewList, 'CustomSubUIViewUI', (x) => x.parentCollectionName != null && x.parentCollectionName.toUpperCase() == expectedPath);
    if (storedRow == null) {
      missingRows.push(expectedRow);
    }
  }
  return missingRows;
}

/**
 * Get original property value.
 * 
 * @param viewModelState View model state.
 * @param propertyName Property name.
 * @param getter Getter.
 */
export function uiCustomizationGetOriginalPropertyValue(viewModelState: ViewModelState, propertyName: string, getter: (arg1: ViewModelPropertyState) => (boolean | null | undefined)): (boolean | null | undefined) {
  let viewModelPropertyState: (ViewModelPropertyState | null | undefined) = getFirstArrayElementOrDefault(viewModelState.properties, 'ViewModelPropertyState', (p) => p.name == propertyName);
  return viewModelPropertyState != null ? getter(viewModelPropertyState) : null;
}

/**
 * Get owner view model.
 * 
 * @param viewModel View model.
 * @param subViewName Sub view name.
 */
export function uiCustomizationGetOwnerViewModel(viewModel: ViewModel<Model>, subViewName: (string | null | undefined)): ViewModel<Model> {
  let ownerViewModel: (ViewModel<Model> | null | undefined) = cast<ViewModel<Model>>(viewModel.viewContext.get('OwnerViewModel'));
  if (ownerViewModel == null) {
    throw new Error(getApplicationResourceManager('TaskScheduler').get('modules.UICustomization.ParentViewModelNotFound'));
  }
  if (subViewName == null) {
    return ownerViewModel;
  }
  return uiCustomizationResolveSubUIViewModel(ownerViewModel, subViewName);
}

/**
 * Get style rule key.
 * 
 * @param styleRule Style rule.
 */
export function uiCustomizationGetStyleRuleKey(styleRule: CustomUIViewStyleRuleUI): string {
  let applicationCondition: (string | null | undefined) = styleRule.applicationCondition;
  let applicationConditionObject: (Filter | null | undefined) = styleRule.applicationConditionObject;
  if (applicationConditionObject != null) {
    let condition = cast<Filter>(applicationConditionObject.clone());
    applicationCondition = condition.isEmpty ? null : condition.convertToJSON();
  }
  return JSON.stringify([styleRule.executionOrder, styleRule.propertyName, applicationCondition, styleRule.lightBackgroundColor, styleRule.lightForegroundColor, styleRule.darkBackgroundColor, styleRule.darkForegroundColor, styleRule.iconName, styleRule.contentDisplayMode, styleRule.horizontalAlign, styleRule.bold, styleRule.italic, styleRule.underline, styleRule.strikethrough]);
}

/**
 * Get sub view style rule key.
 * 
 * @param styleRule Style rule.
 */
export function uiCustomizationGetSubViewStyleRuleKey(styleRule: CustomSubUIViewStyleRuleUI): string {
  let applicationCondition: (string | null | undefined) = styleRule.applicationCondition;
  let applicationConditionObject: (Filter | null | undefined) = styleRule.applicationConditionObject;
  if (applicationConditionObject != null) {
    let condition = cast<Filter>(applicationConditionObject.clone());
    applicationCondition = condition.isEmpty ? null : condition.convertToJSON();
  }
  return JSON.stringify([styleRule.executionOrder, styleRule.propertyName, applicationCondition, styleRule.lightBackgroundColor, styleRule.lightForegroundColor, styleRule.darkBackgroundColor, styleRule.darkForegroundColor, styleRule.iconName, styleRule.contentDisplayMode, styleRule.horizontalAlign, styleRule.bold, styleRule.italic, styleRule.underline, styleRule.strikethrough]);
}

/**
 * Have same keys.
 * 
 * @param currentKeys Current keys.
 * @param storedKeys Stored keys.
 */
export function uiCustomizationHaveSameKeys(currentKeys: string[], storedKeys: string[]): boolean {
  return currentKeys.length == storedKeys.length && currentKeys.every((key) => currentKeys.filter((k) => k == key).length == storedKeys.filter((k) => k == key).length);
}

/**
 * Init custom sub view.
 * 
 * @param customView Custom view.
 * @param ownerViewModel Owner view model.
 * @param isCustomViewNewOrSelected Is custom view new or selected.
 */
export function uiCustomizationInitCustomSubView(customView: CustomSubUIViewUI, ownerViewModel: ViewModel<Model>, isCustomViewNewOrSelected: boolean): void {
  customView.originalOwnerViewModelState = ownerViewModel.customViewManager.getOriginalViewModelState(ownerViewModel);
  if (!isCustomViewNewOrSelected) {
    uiCustomizationInitMissingSubViewProperties(customView, ownerViewModel);
    return ;
  }
  customView.dataSorts = ownerViewModel.dataSorts.length > 0 ? (ownerViewModel.dataSorts.map((s) => s.toString())).join(',') : null;
  for (let property of [...ownerViewModel.getDisplayableProperties()].sort((a,b) => compareValue(((p) => p.datagridPosition)(a),((p) => p.datagridPosition)(b)))) {
    let customViewProperty = getFirstArrayElementOrDefault(customView.propertyList, 'CustomSubUIViewPropertyUI', (c) => c.name == property.name);
    if (customViewProperty == null) {
      customViewProperty = new CustomSubUIViewPropertyUI({name : property.name, customSubUIViewId : customView.id});
      customView.propertyList.push(customViewProperty);
    }
    if (customViewProperty.datagridCaption == null) {
      customViewProperty.datagridCaption = toRecord(new Map<string, string>([
  [getLocalizator('TaskScheduler').currentLanguage, property.datagridCaption]
  ]));
    } else if (!Object.hasOwn(customViewProperty.datagridCaption, getLocalizator('TaskScheduler').currentLanguage)) {
      customViewProperty.datagridCaption = Localizator.withTranslation(customViewProperty.datagridCaption, getLocalizator('TaskScheduler').currentLanguage, property.datagridCaption);
    }
    customViewProperty.datagridVisible = property.datagridVisible;
    customViewProperty.datagridPosition = property.datagridPosition;
    customViewProperty.ellipsis = property.ellipsis;
    customViewProperty.datagridFrozen = property.datagridFrozen;
    customViewProperty.datagridMovable = property.datagridMovable;
    customViewProperty.datagridColumnWidth = property.datagridWidth;
    customViewProperty.horizontalAlign = uiCustomizationConvertContentAlign(property.horizontalAlign);
    if (customViewProperty.caption == null) {
      customViewProperty.caption = toRecord(new Map<string, string>([
  [getLocalizator('TaskScheduler').currentLanguage, property.caption]
  ]));
    } else if (!Object.hasOwn(customViewProperty.caption, getLocalizator('TaskScheduler').currentLanguage)) {
      customViewProperty.caption = Localizator.withTranslation(customViewProperty.caption, getLocalizator('TaskScheduler').currentLanguage, property.caption);
    }
    customViewProperty.formVisible = property.isFormVisibleExpression() ? false : property.getFormVisible(null);
    customViewProperty.readonly = property.isReadOnlyExpression() ? false : property.getReadOnly(null);
    customViewProperty.required = property.isRequiredExpression() ? false : property.getRequired(null);
    customViewProperty.tabStop = property.isTabStopExpression() ? true : property.getTabStop(null);
  }
  let currentParameters: CustomSubUIViewEntityViewParameterUI[] = [];
  for (let parameter of ownerViewModel.getEntityViewParameters()) {
    if (parameter.saveInCustomView) {
      currentParameters.push(new CustomSubUIViewEntityViewParameterUI({name : parameter.name, value : parameter.value != null ? JSON.stringify(parameter.value) : null}));
    }
  }
  let areParametersUnchanged = uiCustomizationHaveSameKeys([...currentParameters.map((p) => uiCustomizationGetEntityViewParameterKey(p.name, p.value))], [...customView.entityViewParameterList.map((p) => uiCustomizationGetEntityViewParameterKey(p.name, p.value))]);
  if (!areParametersUnchanged) {
    customView.entityViewParameterList.clear();
    for (let currentParameter of currentParameters) {
      customView.entityViewParameterList.push(currentParameter);
    }
  }
  let currentStyleRules: CustomSubUIViewStyleRuleUI[] = [];
  for (let userStyleRule of ownerViewModel.userStyleRules) {
    let applicationCondition: (Filter | null | undefined) = cast<(Filter | null | undefined)>((userStyleRule.applicationCondition?.clone()));
    currentStyleRules.push(new CustomSubUIViewStyleRuleUI({customSubUIViewId : customView.id, enabled : true, executionOrder : userStyleRule.options.executionOrder, propertyName : userStyleRule.options.propertyName, applicationCondition : (applicationCondition?.isEmpty) == false ? applicationCondition.convertToJSON() : null, applicationConditionObject : applicationCondition ?? new Filter(), lightBackgroundColor : userStyleRule.options.lightBackgroundColor, lightForegroundColor : userStyleRule.options.lightForegroundColor, darkBackgroundColor : userStyleRule.options.darkBackgroundColor, darkForegroundColor : userStyleRule.options.darkForegroundColor, iconName : userStyleRule.options.iconName, contentDisplayMode : uiCustomizationConvertContentDisplayMode(userStyleRule.options.contentDisplayMode), horizontalAlign : uiCustomizationConvertContentAlign(userStyleRule.options.horizontalAlign), bold : userStyleRule.options.bold, italic : userStyleRule.options.italic, underline : userStyleRule.options.underline, strikethrough : userStyleRule.options.strikethrough}));
  }
  let areStyleRulesUnchanged = uiCustomizationHaveSameKeys([...currentStyleRules.map((r) => uiCustomizationGetSubViewStyleRuleKey(r))], [...customView.styleRuleList.filter((r) => r.enabled).map((r) => uiCustomizationGetSubViewStyleRuleKey(r))]);
  if (!areStyleRulesUnchanged) {
    customView.styleRuleList.removeAll((r) => r.enabled);
    for (let currentStyleRule of currentStyleRules) {
      customView.styleRuleList.push(currentStyleRule);
    }
  }
}

/**
 * Init custom sub view from original state.
 * 
 * @param customView Custom view.
 * @param ownerViewModel Owner view model.
 */
export function uiCustomizationInitCustomSubViewFromOriginalState(customView: CustomSubUIViewUI, ownerViewModel: ViewModel<Model>): void {
  let originalViewModelState: (ViewModelState | null | undefined) = ownerViewModel.customViewManager.getOriginalViewModelState(ownerViewModel);
  if (originalViewModelState == null) {
    uiCustomizationInitCustomSubView(customView, ownerViewModel, true);
    return ;
  }
  customView.originalOwnerViewModelState = originalViewModelState;
  customView.dataSorts = originalViewModelState.sorts.length > 0 ? (originalViewModelState.sorts.map((s) => s.toString())).join(',') : null;
  uiCustomizationInitMissingSubViewProperties(customView, ownerViewModel);
}

/**
 * Init custom view.
 * 
 * @param customView Custom view.
 * @param ownerViewModel Owner view model.
 * @param isCustomViewNewOrSelected Is custom view new or selected.
 */
export function uiCustomizationInitCustomView(customView: CustomUIViewCreateUI, ownerViewModel: ViewModel<Model>, isCustomViewNewOrSelected: boolean): void {
  customView.originalOwnerViewModelState = ownerViewModel.customViewManager.getOriginalViewModelState(ownerViewModel);
  if (!isCustomViewNewOrSelected) {
    for (let property of [...ownerViewModel.getFilterableProperties()].sort((a,b) => compareValue(((p) => p.filterPosition)(a),((p) => p.filterPosition)(b)))) {
      let customViewFilter = getFirstArrayElementOrDefault(customView.dataFilterList, 'CustomUIViewDataFilterUI', (f) => f.name == property.name);
      if (customViewFilter != null) {
        customViewFilter.caption = property.caption;
        if (customViewFilter.defaultOperator == null) {
          let propertyFilterDefaultOperator: (string | null | undefined) = typeEquivalenceModuleFilterOperatorFromCoreFilterOperator(property.filterDefaultOperator);
          customViewFilter.defaultOperator = propertyFilterDefaultOperator;
        }
      }
    }
    return ;
  }
  customView.uiViewName = ownerViewModel.uiViewName;
  customView.userId = getApplicationContext('TaskScheduler').getValue('UserIdentifier', getDefaultValueByType('string') as string);
  let isLookupModal = ownerViewModel.viewContext.has('neos-lookup-item');
  if (!isLookupModal) {
    let filter = ownerViewModel.dataFilter.extractWritableFilter().toSimplifiedFilter();
    let quickSearchFilter: (Filter | null | undefined) = filter.find('isMatch');
    if (quickSearchFilter != null) {
      filter.remove(quickSearchFilter);
    }
    customView.dataFilter = !filter.isEmpty ? filter.convertToJSON() : null;
    customView.dataFilterObject = filter;
  } else {
    customView.dataFilter = null;
    customView.dataFilterObject = new Filter();
  }
  for (let property of [...ownerViewModel.getFilterableProperties()].sort((a,b) => compareValue(((p) => p.filterPosition)(a),((p) => p.filterPosition)(b)))) {
    let customViewFilter = getFirstArrayElementOrDefault(customView.dataFilterList, 'CustomUIViewDataFilterUI', (f) => f.name == property.name);
    let propertyFilterDefaultOperator: (string | null | undefined) = typeEquivalenceModuleFilterOperatorFromCoreFilterOperator(property.filterDefaultOperator);
    if (customViewFilter != null) {
      customViewFilter.caption = property.caption;
      customViewFilter.visible = property.filterVisible;
      customViewFilter.defaultOperator = propertyFilterDefaultOperator;
      customViewFilter.panelVisible = property.filterVisibleInPanel;
      customViewFilter.component = uiCustomizationConvertFilterComponent(property.filterComponent);
      customViewFilter.position = property.filterPosition;
    } else {
      customView.dataFilterList.push(new CustomUIViewDataFilterUI({customUIViewId : customView.id, name : property.name, caption : property.caption, visible : property.filterVisible, defaultOperator : propertyFilterDefaultOperator, panelVisible : property.filterVisibleInPanel, component : uiCustomizationConvertFilterComponent(property.filterComponent), position : property.filterPosition}));
    }
  }
  customView.dataGroups = ownerViewModel.groupDescriptions.length > 0 ? (ownerViewModel.groupDescriptions.map((s) => s)).join(',') : null;
  customView.dataSorts = ownerViewModel.dataSorts.length > 0 ? (ownerViewModel.dataSorts.map((s) => s.toString())).join(',') : null;
  for (let property of [...ownerViewModel.getDisplayableProperties()].sort((a,b) => compareValue(((p) => p.datagridPosition)(a),((p) => p.datagridPosition)(b)))) {
    let customViewProperty = getFirstArrayElementOrDefault(customView.propertyList, 'CustomUIViewPropertyUI', (c) => c.name == property.name);
    if (customViewProperty == null) {
      customViewProperty = new CustomUIViewPropertyUI({customUIViewId : customView.id, name : property.name});
      customView.propertyList.push(customViewProperty);
    }
    if (customViewProperty.datagridCaption == null) {
      customViewProperty.datagridCaption = toRecord(new Map<string, string>([
  [getLocalizator('TaskScheduler').currentLanguage, property.datagridCaption]
  ]));
    } else if (!Object.hasOwn(customViewProperty.datagridCaption, getLocalizator('TaskScheduler').currentLanguage)) {
      customViewProperty.datagridCaption = Localizator.withTranslation(customViewProperty.datagridCaption, getLocalizator('TaskScheduler').currentLanguage, property.datagridCaption);
    }
    customViewProperty.datagridVisible = property.datagridVisible;
    customViewProperty.datagridPosition = property.datagridPosition;
    customViewProperty.ellipsis = property.ellipsis;
    customViewProperty.datagridFrozen = property.datagridFrozen;
    customViewProperty.datagridMovable = property.datagridMovable;
    customViewProperty.datagridColumnWidth = property.datagridWidth;
    customViewProperty.horizontalAlign = uiCustomizationConvertContentAlign(property.horizontalAlign);
    if (customViewProperty.caption == null) {
      customViewProperty.caption = toRecord(new Map<string, string>([
  [getLocalizator('TaskScheduler').currentLanguage, property.caption]
  ]));
    } else if (!Object.hasOwn(customViewProperty.caption, getLocalizator('TaskScheduler').currentLanguage)) {
      customViewProperty.caption = Localizator.withTranslation(customViewProperty.caption, getLocalizator('TaskScheduler').currentLanguage, property.caption);
    }
    customViewProperty.formVisible = property.isFormVisibleExpression() ? false : property.getFormVisible(null);
    customViewProperty.readonly = property.isReadOnlyExpression() ? false : property.getReadOnly(null);
    customViewProperty.required = property.isRequiredExpression() ? false : property.getRequired(null);
    customViewProperty.tabStop = property.isTabStopExpression() ? true : property.getTabStop(null);
  }
  let currentParameters: CustomUIViewEntityViewParameterUI[] = [];
  for (let parameter of ownerViewModel.getEntityViewParameters()) {
    if (parameter.saveInCustomView) {
      currentParameters.push(new CustomUIViewEntityViewParameterUI({name : parameter.name, value : parameter.value != null ? JSON.stringify(parameter.value) : null}));
    }
  }
  let areParametersUnchanged = uiCustomizationHaveSameKeys([...currentParameters.map((p) => uiCustomizationGetEntityViewParameterKey(p.name, p.value))], [...customView.entityViewParameterList.map((p) => uiCustomizationGetEntityViewParameterKey(p.name, p.value))]);
  if (!areParametersUnchanged) {
    customView.entityViewParameterList.clear();
    for (let currentParameter of currentParameters) {
      customView.entityViewParameterList.push(currentParameter);
    }
  }
  let currentStyleRules: CustomUIViewStyleRuleUI[] = [];
  for (let userStyleRule of ownerViewModel.userStyleRules) {
    let applicationCondition: (Filter | null | undefined) = cast<(Filter | null | undefined)>((userStyleRule.applicationCondition?.clone()));
    currentStyleRules.push(new CustomUIViewStyleRuleUI({customUIViewId : customView.id, enabled : true, executionOrder : userStyleRule.options.executionOrder, propertyName : userStyleRule.options.propertyName, applicationCondition : (applicationCondition?.isEmpty) == false ? applicationCondition.convertToJSON() : null, applicationConditionObject : applicationCondition ?? new Filter(), lightBackgroundColor : userStyleRule.options.lightBackgroundColor, lightForegroundColor : userStyleRule.options.lightForegroundColor, darkBackgroundColor : userStyleRule.options.darkBackgroundColor, darkForegroundColor : userStyleRule.options.darkForegroundColor, iconName : userStyleRule.options.iconName, contentDisplayMode : uiCustomizationConvertContentDisplayMode(userStyleRule.options.contentDisplayMode), horizontalAlign : uiCustomizationConvertContentAlign(userStyleRule.options.horizontalAlign), bold : userStyleRule.options.bold, italic : userStyleRule.options.italic, underline : userStyleRule.options.underline, strikethrough : userStyleRule.options.strikethrough}));
  }
  let areStyleRulesUnchanged = uiCustomizationHaveSameKeys([...currentStyleRules.map((r) => uiCustomizationGetStyleRuleKey(r))], [...customView.styleRuleList.filter((r) => r.enabled).map((r) => uiCustomizationGetStyleRuleKey(r))]);
  if (!areStyleRulesUnchanged) {
    customView.styleRuleList.removeAll((r) => r.enabled);
    for (let currentStyleRule of currentStyleRules) {
      customView.styleRuleList.push(currentStyleRule);
    }
  }
}

/**
 * Init missing sub view properties.
 * 
 * @param customView Custom view.
 * @param ownerViewModel Owner view model.
 */
export function uiCustomizationInitMissingSubViewProperties(customView: CustomSubUIViewUI, ownerViewModel: ViewModel<Model>): void {
  let originalState: (ViewModelState | null | undefined) = customView.originalOwnerViewModelState;
  if (originalState == null) {
    return ;
  }
  for (let property of [...ownerViewModel.getDisplayableProperties()].sort((a,b) => compareValue(((p) => p.datagridPosition)(a),((p) => p.datagridPosition)(b)))) {
    let existingProperty = getFirstArrayElementOrDefault(customView.propertyList, 'CustomSubUIViewPropertyUI', (c) => c.name == property.name);
    let propertyState: (ViewModelPropertyState | null | undefined) = getFirstArrayElementOrDefault(originalState.properties, 'ViewModelPropertyState', (p) => p.name == property.name);
    if (existingProperty != null || propertyState == null) {
      continue;
    }
    customView.propertyList.push(new CustomSubUIViewPropertyUI({name : propertyState.name, customSubUIViewId : customView.id, caption : toRecord(new Map<string, string>([
  [getLocalizator('TaskScheduler').currentLanguage, propertyState.caption]
  ])), datagridCaption : toRecord(new Map<string, string>([
  [getLocalizator('TaskScheduler').currentLanguage, propertyState.datagridCaption]
  ])), datagridVisible : propertyState.datagridVisible, datagridPosition : propertyState.datagridPosition, datagridFrozen : propertyState.datagridFrozen, datagridMovable : propertyState.datagridMovable, datagridColumnWidth : propertyState.datagridWidth, ellipsis : propertyState.ellipsis, horizontalAlign : uiCustomizationConvertContentAlign(propertyState.horizontalAlign), formVisible : propertyState.formVisible ?? false, readonly : propertyState.readonly ?? false, required : propertyState.required ?? false, tabStop : propertyState.tabStop ?? true}));
  }
}

/**
 * Init sub UI view rows.
 * 
 * @param customView Custom view.
 * @param ownerViewModel Owner view model.
 * @param isCustomViewNewOrSelected Is custom view new or selected.
 */
export function uiCustomizationInitSubUIViewRows(customView: CustomUIViewCreateUI, ownerViewModel: ViewModel<Model>, isCustomViewNewOrSelected: boolean): void {
  let expectedRows = uiCustomizationBuildSubUIViewRows(ownerViewModel, []);
  for (let row of customView.subUIViewList) {
    if (!row.parentCollectionName) {
      continue;
    }
    let rowPath = row.parentCollectionName.toUpperCase();
    let expectedRow = getFirstArrayElementOrDefault(expectedRows, 'CustomSubUIViewUI', (x) => x.parentCollectionName.toUpperCase() == rowPath);
    if (expectedRow == null) {
      continue;
    }
    let rowViewModel = uiCustomizationResolveSubUIViewModel(ownerViewModel, row.parentCollectionName);
    uiCustomizationInitCustomSubView(row, rowViewModel, isCustomViewNewOrSelected);
  }
}

/**
 * Is custom view disabled.
 * 
 * @param viewModel View model.
 */
export function uiCustomizationIsCustomViewDisabled(viewModel: ViewModel<Model>): boolean {
  return cast<(boolean | null | undefined)>(viewModel.contextData.get('DisableCustomView')) == true;
}

/**
 * Max sub UI view depth.
 */
export function uiCustomizationMaxSubUIViewDepth(): number {
  return 5;
}

/**
 * On layout updated async.
 * 
 * @param args Args.
 */
export async function uiCustomizationOnLayoutUpdatedAsync(args: UIViewEventArgs): Promise<void> {
  let mainViewModel = args.viewModel;
  while (!mainViewModel.isMain) {
    mainViewModel = mainViewModel.parentViewModel;
  }
  if (uiCustomizationIsCustomViewDisabled(mainViewModel)) {
    return ;
  }
  let autoCustomView = new CustomUIViewCreateUI();
  autoCustomView.id = emptyGuid;
  autoCustomView.name = { [getLocalizator('TaskScheduler').defaultLanguage]: 'Default' };
  uiCustomizationInitCustomView(autoCustomView, mainViewModel, true);
  for (let autoCustomSubView of uiCustomizationBuildSubUIViewRows(mainViewModel, [])) {
    autoCustomView.subUIViewList.push(autoCustomSubView);
  }
  uiCustomizationInitSubUIViewRows(autoCustomView, mainViewModel, true);
  try {
    mainViewModel.currentCustomViewId = await getApiClient('TaskScheduler').callPostServerMethodAsync<string>('$neos/save-auto-custom-view', ApiParameterBinding.bodyProperty, { autoCustomView: cast<CustomUIViewDetailView>(autoCustomView.toApiRequestData(false)) });
  }
  catch(ex) {
    if (ex instanceof Error) {
      console.log(stringFormat('Error while saving the auto custom view: {0}', ex));
    } else {
      throw ex;
    }
  }
}

/**
 * Parse style rule conditions async.
 * 
 * @param customView Custom view.
 * @param ownerViewModel Owner view model.
 */
export async function uiCustomizationParseStyleRuleConditionsAsync(customView: CustomUIViewCreateUI, ownerViewModel: ViewModel<Model>): Promise<void> {
  for (let styleRule of [...customView.styleRuleList.filter((r) => r.enabled)]) {
    let applicationCondition: (string | null | undefined) = styleRule.applicationCondition;
    if (applicationCondition == null) {
      continue;
    }
    let condition: (Filter | null | undefined) = null;
    try {
      condition = await ownerViewModel.convertFilterFromJsonAsync(applicationCondition);
    }
  catch {
      condition = null;
    }
    if (condition != null) {
      styleRule.applicationConditionObject = condition;
    }
  }
  let expectedRows = uiCustomizationBuildSubUIViewRows(ownerViewModel, []);
  for (let row of customView.subUIViewList) {
    if (!row.parentCollectionName) {
      continue;
    }
    let rowPath = row.parentCollectionName.toUpperCase();
    let expectedRow = getFirstArrayElementOrDefault(expectedRows, 'CustomSubUIViewUI', (x) => x.parentCollectionName.toUpperCase() == rowPath);
    if (expectedRow == null) {
      continue;
    }
    let rowViewModel = uiCustomizationResolveSubUIViewModel(ownerViewModel, row.parentCollectionName);
    for (let subStyleRule of [...row.styleRuleList.filter((r) => r.enabled)]) {
      let subApplicationCondition: (string | null | undefined) = subStyleRule.applicationCondition;
      if (subApplicationCondition == null) {
        continue;
      }
      let subCondition: (Filter | null | undefined) = null;
      try {
        subCondition = await rowViewModel.convertFilterFromJsonAsync(subApplicationCondition);
      }
  catch {
        subCondition = null;
      }
      if (subCondition != null) {
        subStyleRule.applicationConditionObject = subCondition;
      }
    }
  }
}

/**
 * Refresh groups ui view.
 * 
 * @param viewModel View model.
 * @param tabIdentifier Tab identifier.
 * @param parentCollectionName Parent collection name.
 * @param dataGroups Data groups.
 * @param setValueCallback Set value callback.
 */
export function uiCustomizationRefreshGroupsUiView(viewModel: ViewModel<Model>, tabIdentifier: string, parentCollectionName: (string | null | undefined), dataGroups: (string | null | undefined), setValueCallback: (args1:string) => void): void {
  let groupsViewModel = cast<CustomUIViewGroupsUIViewModel>(viewModel.findViewModelById<CustomUIViewGroupsUI>(tabIdentifier));
  if (groupsViewModel != null) {
    let ownerViewModel = uiCustomizationGetOwnerViewModel(viewModel, parentCollectionName);
    let sortablesPropertiesList: [string, string][] = [];
    for (let property of [...ownerViewModel.getDisplayableProperties().filter((p) => p.serverSideSortable)].sort((a,b) => compareValue(((p) => p.name)(a),((p) => p.name)(b)))) {
      let tuple = cast<[string, string]>([property.name, property.datagridCaption]);
      sortablesPropertiesList.push(tuple);
    }
    groupsViewModel.methods.refreshThisUiView(sortablesPropertiesList, dataGroups ?? '', setValueCallback);
  }
}

/**
 * Resolve sub UI view model.
 * 
 * @param ownerViewModel Owner view model.
 * @param relationPath Relation path.
 */
export function uiCustomizationResolveSubUIViewModel(ownerViewModel: ViewModel<Model>, relationPath: string): ViewModel<Model> {
  let current = ownerViewModel;
  for (let segment of relationPath.split('/')) {
    current = current.getSubViewModel<Model>(segment);
  }
  return current;
}

/**
 * Set property list sort.
 * 
 * @param viewModel View model.
 * @param ownerViewModel Owner view model.
 */
export function uiCustomizationSetPropertyListSort(viewModel: ViewModel<Model>, ownerViewModel: ViewModel<Model>): void {
  if (ownerViewModel.monoRecord) {
    viewModel.setSort(toCamelCase('Name'), SortDirection.asc);
  } else {
    viewModel.setSort(toCamelCase('DatagridPosition'), SortDirection.asc);
  }
}

/**
 * Unselect sub UI view nodes.
 * 
 * @param nodes Nodes.
 */
export function uiCustomizationUnselectSubUIViewNodes(nodes: (CustomSubUIView[] | null | undefined)): void {
  if (nodes == null) {
    return ;
  }
  for (let node of nodes) {
    node.selected = false;
    uiCustomizationUnselectSubUIViewNodes(node.children);
  }
}
